import React, { useState } from "react";
import { Area, Bar, ComposedChart, Legend, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, Brush, Scatter, Cell } from "recharts";
import { formatter, formatToMonthDay, isPercentageFormatter } from "../../utils/formatter";
import { CurveType } from "recharts/types/shape/Curve";
import { AxisInterval } from "recharts/types/util/types";

interface StackConfig {
    enabled: boolean;
    id?: string;
}

interface FilterConfig {
    enabled: boolean;
    config: any
}
interface SelectedPoint {
    subCategory: string;
    segment: string;
  }

interface GraphComponentProps {
    title: string;
    data: {}[];
    graphTypes: string[];
    dataKey: string[];
    xAxisKey?: string;
    yAxisKey?: string;
    dataName: string[];
    fill: string[];
    stack?: StackConfig;
    filter?: FilterConfig;
    margin?: {};
    curveType?: CurveType;
    scrollable? : boolean;
    onClick?: (event: any) => void;
    width?: number;
    interval?: AxisInterval;
    isRequiredTickFormatter?: boolean;
    isRequiredYAxisFormatter?: boolean;
    isRequiredXAxisFormatter?: boolean;
    customType?: string;
    xAxisLabel?: string; 
    yAxisLabel?: string; 
    yAxisLabelSecondAxis?: string;
    selectedPoint?: SelectedPoint | null;
    barGap?: number;
    isRequiredYaxisid?: boolean;
}

const looksLikeDateLabel = (value: unknown): boolean => {
    if (typeof value !== "string") {
        return false;
    }

    const normalized = value.trim();
    if (!normalized) {
        return false;
    }

    // Limit date auto-formatting to explicit, well-known date patterns only.
    const numericDatePattern = /^(\d{4}[-/]\d{1,2}[-/]\d{1,2}|\d{1,2}[-/]\d{1,2}([-/]\d{2,4})?)$/;

    const monthNames = new Set([
        "jan", "january", "feb", "february", "mar", "march", "apr", "april", "may",
        "jun", "june", "jul", "july", "aug", "august", "sep", "sept", "september",
        "oct", "october", "nov", "november", "dec", "december",
    ]);

    const dateTokens = normalized.replace(",", "").split(/\s+/);
    const looksLikeMonthDate =
        dateTokens.length >= 2 &&
        monthNames.has((dateTokens[0] || "").toLowerCase()) &&
        /^\d{1,2}$/.test(dateTokens[1] || "");

    if (!numericDatePattern.test(normalized) && !looksLikeMonthDate) {
        return false;
    }

    return !Number.isNaN(Date.parse(normalized));
};



// Custom Legend
const CustomLegend = (props: any) => {
    const { payload, customType } = props;

    if (customType === "scatter") {
        const segmentColors = {
            A: "#046A38", 
            B: "#8ED07F", 
            C: "#CFE4A8", 
            D: "#666666", 
            // E: "#93E9BE", 
        };

        return (
            <ul className="flex items-center gap-2">
                <li className="flex items-center">
                    <span className="text-[#949494]">Segment</span>
                    {Object.entries(segmentColors).map(([segment, color]) => (
                        <span 
                            key={segment}
                            className="ml-2 font-semibold"
                            style={{ color: color }}
                        >
                            {segment}
                        </span>
                    ))}
                </li>
            </ul>
        );
    }

    return (
        <ul className="flex items-center gap-2">
            {payload.map((entry: any, index: number) => (
                <li key={`item-${index}`} className="flex items-center">
                    {entry.type === 'line' ? (
                        <>
                            <svg width="30" height="8">
                                <line x1="0" y1="4" x2="10" y2="4" stroke={entry?.color} strokeWidth="1.5" />
                                <polygon points="12,1 16,4 12,7 8,4" fill={entry?.color} />
                                <line x1="15" y1="4" x2="25" y2="4" stroke={entry?.color} strokeWidth="1.5" />
                            </svg>
                        </>
                    ) : (
                        <svg width="10" height="10">
                            <rect x="0" y="0" width="10" height="10" fill={entry.color} />
                        </svg>
                    )}
                    <span style={{ color: entry.color }} className="ml-2 font-semibold font-[0.6vw]">
                        {entry.value}
                    </span>
                </li>
            ))}
        </ul>
    );
};


// Custom Tooltip
const CustomTooltip = (props: any) => {
    const { active, payload, xAxisKey, customType } = props;
    if (active && payload && payload.length && !customType) {
        return (
            <div className="bg-[#fff] border-[1px] border-[#BEBEBE] p-2 rounded rs-bg">
                <p className="text-black text-fs16 font-bold mb-2">
                <span>
                    {looksLikeDateLabel(props?.label) ? formatToMonthDay(props.label) : props?.label}
                </span>
                </p>
                <div className="grid grid-cols-[auto_auto] gap-2">
                    {payload?.map(($: any) => (
                        <div className="flex flex-col" key={$?.name}>
                            <p className="text-black text-fs14">
                                {$?.name}
                            </p>
                            <p style={{ color: $.color }} className="text-black font-semibold chart-legend">
                                {["AvgAdChange","adAverage"].includes($?.dataKey) ? (formatter($?.value) + "%") : formatter($?.value)}
                            </p>
                        </div>
                    ))}
                </div>
            </div>
        );
    }else if(customType === "scatter" && active && payload && payload.length) {
        const data = payload[0].payload;
        return (
            <div className="bg-[rgba(35,35,35,0.95)] p-3 rounded">
                <div className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-1 text-white">
                    <span className="text-[#aaa]">SubCategory</span>
                    <span className="text-right">{data.subCategory}</span>
                    
                    <span className="text-[#aaa]">Segment</span>
                    <span className="text-right">{data.final_segment_erp}</span>
                    
                    <span className="text-[#aaa]">Accuracy</span>
                    <span className="text-right">{`${(data.dcAccuracy ).toFixed(0)}%`}</span>
                    
                    {data?.dcBias !== undefined ? (
                    <>
                        <span className="text-[#aaa]">BIAS</span>
                        <span className="text-right">{`${(data.dcBias).toFixed(2)}%`}</span>
                    </>
                ) : (
                    <>
                        <span className="text-[#aaa]">Total Pounds</span>
                        <span className="text-right">{data.TotalPounds?.toFixed(2)}</span>
                    </>
                )}                    
                    <span className="text-[#aaa]">Category</span>
                    <span className="text-right">{data.category}</span>
                </div>
            </div>
        );
    }
    return null;
};
  

const Graph: React.FC<GraphComponentProps> = ({
    title,
    data,
    graphTypes,
    xAxisKey,
    yAxisKey,
    dataKey,
    dataName,
    fill,
    stack,
    filter,
    margin,
    curveType = "monotone",
    scrollable=false,
    onClick,
    width,
    interval = "equidistantPreserveStart",
    isRequiredTickFormatter = false,
    isRequiredYAxisFormatter = false,
    isRequiredXAxisFormatter = false,
    customType,
    yAxisLabel,
    yAxisLabelSecondAxis,
    xAxisLabel,
    selectedPoint,
    barGap = 10,
    isRequiredYaxisid = false
}) => {

    let marginConfig = {};
    let legendWrapper = { top: 0 };

    if(filter){
        if (filter?.enabled) {
            marginConfig = { top: 15, right: 20, bottom: -10, left: 0 };
            legendWrapper = { top: 0 };
        } else {
            marginConfig = { top: 15, right: 20, bottom: -10, left: 0 };
        }
    }
    else{
        if(margin){
            marginConfig = margin;
        } else {
            marginConfig = { top: 15, right: 20, bottom: 0, left: 0 }
        }
    }

    return <div className={`w-[100%] h-[100%] bg-[#FFF] shadow rounded px-[2%] py-[10px] border-[1px] border-[#BEBEBE] flex flex-col grow`}>
        <div className="grid grid-cols-[1.5fr_auto] justify-between items-center mb-[0.25vw]">
            <span className="text-mainGreen font-semibold chart-title self-start">
                {title}
            </span>
        </div>
        <div className="flex h-[100%]">
            <div className="grow h-full">
                <ResponsiveContainer width={"100%"} height={"100%"}>
                    <ComposedChart data={data} margin={marginConfig} barCategoryGap={10} barGap={barGap} stackOffset="sign">
                    <XAxis
                        fontSize={"0.8vw"}
                        dataKey={xAxisKey}
                        interval={interval}
                        tickFormatter={(data) => {
                            if (xAxisKey === "SubCategory" || xAxisKey === "Category" ) {
                                return data?.length > 6 ? data.substring(0, 4) + ".." : data;
                            }
                            if (xAxisKey === "BranchId") {
                                return data;
                            }
                            if (isRequiredXAxisFormatter) {
                              return isPercentageFormatter(data);
                            }
                        
                            // Check if the data is a date and format it
                                                        if (looksLikeDateLabel(data)) {
                              return formatToMonthDay(data); // Format valid dates as "Aug 30"
                            }
                        
                            // For long strings, truncate if `isRequiredTickFormatter` is true
                            return data?.length > 10 && isRequiredTickFormatter
                              ? data.substring(0, 4) + ".."
                              : data;
                          }}
                        type="category"
                        stroke="#949494"
                        label={xAxisLabel ? {
                            value: xAxisLabel,
                            position: "insideBottom",
                            offset: -10, 
                            style: { fontSize: 14 },
                          } : undefined}
                    />
                        <YAxis  yAxisId="left"  dataKey={yAxisKey} interval="equidistantPreserveStart" fontSize={"0.80vw"} tickCount={10} tickFormatter={isRequiredYAxisFormatter ? isPercentageFormatter : formatter} stroke="#949494" 
                         label={yAxisLabel ? {
                            value: yAxisLabel,
                            angle: -90,
                            position: "insideLeft",
                            dx: -10,
                            offset: 6,
                            style: { fontSize: "0.80vw", fill: "#BBED9B", fontWeight: "bold" },
                        } : undefined} 
                        />
                         {isRequiredYaxisid && <YAxis    interval="equidistantPreserveStart"  yAxisId="right" orientation="right" fontSize={"0.80vw"} tickCount={10}   tickFormatter={isRequiredYAxisFormatter ? isPercentageFormatter : formatter} stroke="#949494" 
                                                 label={yAxisLabelSecondAxis ? {
                                                    value: yAxisLabelSecondAxis,
                                                    angle: -90,
                                                    position: "insideLeft",
                                                    dx: 55,
                                                    offset: 6,
                                                    style: { fontSize: "0.80vw", fill: "#027148", fontWeight: "bold" },
                                                } : undefined} 
                                                />}
                        <Tooltip content={<CustomTooltip xAxisKey={xAxisKey} customType={customType} />} />
                        {scrollable && Array.isArray(data) && data?.length>20 && <Brush
                            dataKey="forecastDate"
                            alwaysShowText={false}
                            startIndex={0}
                            endIndex={10}
                            height={6}
                            tickFormatter={(tick: any) => (0 ? tick : "")}
                            opacity={1}
                            fill="white"
                            strokeOpacity={1}
                            fillOpacity={1}
                            stroke="black"
                            travellerWidth={0}
                        />}
                        <Legend
                            content={<CustomLegend  customType={customType} />}
                            align="left"
                            verticalAlign="top"
                            iconSize={8}
                            wrapperStyle={{ left: 0, marginBottom: "10px",  ...legendWrapper}}
                        />
                        {Array.isArray(graphTypes) && graphTypes.map((type, index) => {
                            const dataKeyCurrent = dataKey[index];
                            const dataNameCurrent = dataName[index];
                            const fillCurrent = fill[index];

                            if (type === "line") {
                                return (
                                    <Line
                                        key={dataKeyCurrent}
                                        name={dataNameCurrent}
                                        dataKey={dataKeyCurrent}
                                        stroke={fillCurrent}
                                        fill={fillCurrent}
                                        strokeWidth={2}
                                        yAxisId={isRequiredYaxisid ? "right" : "left"}
                                        dot={{ strokeWidth: 1, r: 2 }}
                                        type={curveType}
                                        activeDot={{ stroke: '#012169', fill: 'white', strokeWidth: 2, r: 4 }}
                                    />
                                );
                            } else if (type === "area") {
                                return (
                                    <Area
                                        key={dataKeyCurrent}
                                        name={dataNameCurrent}
                                        dataKey={dataKeyCurrent}
                                        fill={fillCurrent}
                                        stroke={fillCurrent}
                                        fillOpacity={0.2}
                                        yAxisId="left"
                                    />
                                );
                            } else if (type === "bar"){
                                return (
                                    <Bar
                                        key={dataKeyCurrent}
                                        name={dataNameCurrent}
                                        dataKey={dataKeyCurrent}
                                        stroke={fillCurrent}
                                        fill={fillCurrent}
                                        stackId={stack?.enabled ? stack.id : undefined}
                                        maxBarSize={width || 40}
                                        barSize={data?.length == 1 ? 40 : width || 30}
                                        onClick={onClick}
                                        yAxisId="left"
                                    />
                                )
                            }
                            else if (type === "scatter") { 
                                const segmentColors = {
                                    A: "#046A38", 
                                    B: "#8ED07F", 
                                    C: "#CFE4A8", 
                                    D: "#666666", 
                                    E: "#93E9BE", 
                                } as const;
                            
                                const [hoveredPoint, setHoveredPoint] = useState<string | null>(null);

                               
                                return (
                                    <Scatter
                                        key={dataKeyCurrent}
                                        name={dataNameCurrent}
                                        dataKey={dataKeyCurrent}
                                        yAxisId="left"
                                    >
                                        {data.map((entry: any, index: number) => (
                                            <Cell
                                                key={`cell-${index}`}
                                                fill={
                                                    // Check if a point is selected
                                                    selectedPoint
                                                      ? // If selected, only dim points that DON'T match both subCategory AND segment
                                                        selectedPoint.subCategory === entry.subCategory &&
                                                        selectedPoint.segment === entry.final_segment_erp
                                                          ? segmentColors[entry.final_segment_erp as keyof typeof segmentColors] // Keep selected point's color
                                                          : `${segmentColors[entry.final_segment_erp as keyof typeof segmentColors]}80` // Dim other points
                                                      : // If no point is selected, check for hover
                                                        hoveredPoint && hoveredPoint !== entry.subCategory
                                                          ? `${segmentColors[entry.final_segment_erp as keyof typeof segmentColors]}80` // Dim other points on hover
                                                          : segmentColors[entry.final_segment_erp as keyof typeof segmentColors] // Keep normal color for non-hovered points
                                                  }
                                                  
                                                onMouseEnter={() => setHoveredPoint(entry.subCategory)}
                                                onMouseLeave={() => setHoveredPoint(null)}
                                                onClick={() => onClick && onClick(entry)}

                                            />
                                        ))}
                                    </Scatter>
                                );
                            }
                            
                            

                            return null; // To handle cases where the type is neither line nor area (optional)
                        })}
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
        </div>
    </div>
}

export default Graph;